import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import { CommonConfirmationModalData } from '../common-confirmation-modal/common-confirmation-modal.component.model';

@Component({
  selector: 'wm-common-dynamic-table',
  standalone: false,
  templateUrl: './common-dynamic-table.component.html',
  styleUrls: ['./common-dynamic-table.component.scss']
})
export class CommonDynamicTableComponent implements AfterViewInit, OnChanges {
  private readonly ENTITY_DROPDOWN_ESTIMATED_HEIGHT = 360;
  private readonly WORKFLOW_DROPDOWN_ESTIMATED_HEIGHT = 320;
  private readonly DROPDOWN_ESTIMATED_WIDTH = 360;
  private hoverHideTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private pendingWorkflowDeleteIndex: number | null = null;
  private pendingColumnDelete: any | null = null;
  private pendingCellValueDelete: {
    row: any;
    column: any;
    index: number;
  } | null = null;

  @Input() rows: any[] = [];
  @Input() columns: any[] = [];
  @Input() availableWorkflows: any[] = [];
  @Input() entities: any[] = [];
  @Input() dataSetConfig: any;
  @Input() isNoData = false;
  @Input() hasFewData = false;
  @Input() workflowDisplayNameFn: (
    workflowId?: string | null,
    fallback?: string
  ) => string = (_workflowId, fallback = 'Select Workflow') => fallback;

  @Output() addWorkflowRequested = new EventEmitter<void>();
  @Output() deleteWorkflowRequested = new EventEmitter<number>();
  @Output() workflowSelected = new EventEmitter<{
    index: number;
    workflow: any;
  }>();
  @Output() columnSettingsRequested = new EventEmitter<void>();
  @Output() columnEditRequested = new EventEmitter<any>();
  @Output() columnDeleteRequested = new EventEmitter<any>();
  @Output() columnMoveLeftRequested = new EventEmitter<any>();
  @Output() columnMoveRightRequested = new EventEmitter<any>();
  @Output() columnReordered = new EventEmitter<{
    column: any;
    targetColumn: any;
    previousIndex: number;
    currentIndex: number;
  }>();
  @Output() cellDropdownOpened = new EventEmitter<{
    row: any;
    column: any;
  }>();
  @Output() cellEntitySelected = new EventEmitter<{
    row: any;
    column: any;
    entity: any;
  }>();
  @Output() cellValueDeleted = new EventEmitter<{
    row: any;
    column: any;
    index: number;
  }>();
  @Output() rowFiltersChanged = new EventEmitter<{
    event: any;
    row: any;
  }>();

  @ViewChildren('workflowRowElement')
  workflowRowElements: QueryList<ElementRef<HTMLElement>>;

  @ViewChildren('workflowDataRowElement')
  workflowDataRowElements: QueryList<ElementRef<HTMLElement>>;

  activeDropdown: number | null = null;
  activeCellRow: any | null = null;
  activeCellColumn: any | null = null;
  workflowDropdownTop = 0;
  workflowDropdownBottom: number | null = null;
  workflowDropdownLeft = 0;
  dropdownTop = 0;
  dropdownBottom: number | null = null;
  dropdownLeft = 0;
  entitySearch = '';
  filteredEntities: any[] = [];
  hoveredColumnName: string | null = null;
  draggedColumnName: string | null = null;
  dragOverColumnName: string | null = null;
  columnActionsTop = 0;
  columnActionsLeft = 0;
  popoverStateMap: {
    [key: number]: { isOpen: boolean; placement: 'top' | 'bottom' };
  } = {};
  deleteWorkflowConfirmModalData: CommonConfirmationModalData = {
    open: false,
    yesNoBtn: true,
    replaceYesWith: 'DELETE',
    replaceNoWith: 'CANCEL',
    modalDescp: 'Delete workflow?',
    modalSubDescp:
      'This will remove the selected workflow and its configured dataset mappings from this view.',
    status: 'default'
  };
  deleteColumnConfirmModalData: CommonConfirmationModalData = {
    open: false,
    yesNoBtn: true,
    replaceYesWith: 'DELETE',
    replaceNoWith: 'CANCEL',
    modalDescp: 'Delete column?',
    modalSubDescp:
      'This will remove the selected dataset column and its mapped values from this view.',
    status: 'default'
  };
  deleteCellValueConfirmModalData: CommonConfirmationModalData = {
    open: false,
    yesNoBtn: true,
    replaceYesWith: 'DELETE',
    replaceNoWith: 'CANCEL',
    modalDescp: 'Delete mapped value?',
    modalSubDescp:
      'This will remove the selected mapped value from the dataset cell.',
    status: 'default'
  };

  ngAfterViewInit(): void {
    this.workflowRowElements.changes.subscribe(() => this.scheduleRowHeightSync());
    this.workflowDataRowElements.changes.subscribe(() => this.scheduleRowHeightSync());
    this.scheduleRowHeightSync();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entities']) {
      this.updateFilteredEntities();
    }

    if (changes['rows'] || changes['columns']) {
      this.scheduleRowHeightSync();
    }
  }

  getWorkflowDisplayName(
    workflowId?: string | null,
    fallback = 'Select Workflow'
  ): string {
    return this.workflowDisplayNameFn(workflowId, fallback);
  }

  getColumnHeaderText(column: any): string {
    const resultHeader = column?.resultHeader || '';
    const name = column?.name || '';

    if (resultHeader && name && resultHeader !== name) {
      return `${resultHeader} (${name})`;
    }

    return resultHeader || name || '';
  }

  addWorkflow(): void {
    this.addWorkflowRequested.emit();
  }

  deleteWorkflow(index: number): void {
    this.pendingWorkflowDeleteIndex = index;
    this.deleteWorkflowConfirmModalData = {
      ...this.deleteWorkflowConfirmModalData,
      open: true
    };
  }

  confirmDeleteWorkflow(): void {
    if (this.pendingWorkflowDeleteIndex === null) {
      return;
    }

    this.deleteWorkflowRequested.emit(this.pendingWorkflowDeleteIndex);
    this.closeDeleteWorkflowModal();
  }

  closeDeleteWorkflowModal(): void {
    this.pendingWorkflowDeleteIndex = null;
    this.deleteWorkflowConfirmModalData = {
      ...this.deleteWorkflowConfirmModalData,
      open: false
    };
  }

  selectWorkflow(index: number, workflow: any): void {
    this.activeDropdown = null;
    this.workflowSelected.emit({ index, workflow });
  }

  openColumnSettings(): void {
    this.columnSettingsRequested.emit();
  }

  requestColumnEdit(column: any): void {
    this.columnEditRequested.emit(column);
  }

  requestColumnDelete(column: any): void {
    this.pendingColumnDelete = column;
    this.deleteColumnConfirmModalData = {
      ...this.deleteColumnConfirmModalData,
      open: true
    };
  }

  confirmDeleteColumn(): void {
    if (!this.pendingColumnDelete) {
      return;
    }

    this.columnDeleteRequested.emit(this.pendingColumnDelete);
    this.closeDeleteColumnModal();
  }

  closeDeleteColumnModal(): void {
    this.pendingColumnDelete = null;
    this.deleteColumnConfirmModalData = {
      ...this.deleteColumnConfirmModalData,
      open: false
    };
  }

  requestColumnMoveLeft(column: any): void {
    this.columnMoveLeftRequested.emit(column);
  }

  requestColumnMoveRight(column: any): void {
    this.columnMoveRightRequested.emit(column);
  }

  onRowFiltersChange2(event: any, row: any): void {
    this.rowFiltersChanged.emit({ event, row });
  }

  onPopoverStateChange(event: any, index: number): void {
    this.popoverStateMap[index] = event;
    this.scheduleRowHeightSync();
  }

  toggleDropdown(index: number, event: MouseEvent): void {
    if (this.activeDropdown === index) {
      this.activeDropdown = null;
      return;
    }

    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownWidth = this.DROPDOWN_ESTIMATED_WIDTH;
    const spaceBelow = viewportHeight - rect.bottom;
    const shouldOpenUpward =
      spaceBelow < this.WORKFLOW_DROPDOWN_ESTIMATED_HEIGHT &&
      rect.top > this.WORKFLOW_DROPDOWN_ESTIMATED_HEIGHT;

    this.workflowDropdownTop = shouldOpenUpward ? 0 : rect.bottom + 4;
    this.workflowDropdownBottom = shouldOpenUpward
      ? Math.max(12, viewportHeight - rect.top + 4)
      : null;
    this.workflowDropdownLeft = Math.min(
      Math.max(12, rect.left),
      Math.max(12, viewportWidth - dropdownWidth - 12)
    );
    this.activeDropdown = index;
  }

  openCellDropdown(row: any, column: any, event: MouseEvent): void {
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const shouldOpenUpward =
      spaceBelow < this.ENTITY_DROPDOWN_ESTIMATED_HEIGHT &&
      rect.top > this.ENTITY_DROPDOWN_ESTIMATED_HEIGHT;

    this.dropdownTop = shouldOpenUpward ? 0 : rect.bottom + 4;
    this.dropdownBottom = shouldOpenUpward
      ? Math.max(12, viewportHeight - rect.top + 4)
      : null;
    this.dropdownLeft = rect.left;
    this.activeCellRow = row;
    this.activeCellColumn = column;
    this.entitySearch = '';
    this.updateFilteredEntities();
    this.cellDropdownOpened.emit({ row, column });
  }

  onEntitySearchChange(value: string): void {
    this.entitySearch = value || '';
    this.updateFilteredEntities();
  }

  selectCellEntity(row: any, column: any, entity: any): void {
    this.activeCellRow = null;
    this.activeCellColumn = null;
    this.cellEntitySelected.emit({ row, column, entity });
  }

  deleteCellValue(row: any, column: any, index: number): void {
    this.pendingCellValueDelete = { row, column, index };
    this.deleteCellValueConfirmModalData = {
      ...this.deleteCellValueConfirmModalData,
      open: true
    };
  }

  confirmDeleteCellValue(): void {
    if (!this.pendingCellValueDelete) {
      return;
    }

    this.cellValueDeleted.emit(this.pendingCellValueDelete);
    this.closeDeleteCellValueModal();
  }

  closeDeleteCellValueModal(): void {
    this.pendingCellValueDelete = null;
    this.deleteCellValueConfirmModalData = {
      ...this.deleteCellValueConfirmModalData,
      open: false
    };
  }

  trackRowByWorkflow = (_index: number, row: any) =>
    row?.rowKey || `pending-${_index}`;

  trackColumnByName = (_index: number, col: any) => col?.name || _index;

  onHeaderMouseEnter(column: any, event?: MouseEvent): void {
    this.clearHoverHideTimeout();
    this.hoveredColumnName = column?.name || null;
    this.updateColumnActionsPosition(event, column);
  }

  onHeaderMouseLeave(column: any): void {
    if (this.hoveredColumnName === (column?.name || null)) {
      this.scheduleHoveredColumnHide(column);
    }
  }

  onColumnActionsEnter(column: any): void {
    this.clearHoverHideTimeout();
    this.hoveredColumnName = column?.name || null;
  }

  onColumnActionsLeave(column: any): void {
    this.scheduleHoveredColumnHide(column);
  }

  isColumnHovered(column: any): boolean {
    return this.hoveredColumnName === (column?.name || null);
  }

  canDeleteColumn(column: any): boolean {
    // Added by Amisha on 05/21/2026 to let Janus honor the same protected-column
    // behavior as the legacy dataset designer while keeping starter columns deletable.
    return !column?.isStatic;
  }

  canMoveColumnLeft(column: any): boolean {
    return this.getColumnIndex(column) > 0;
  }

  canMoveColumnRight(column: any): boolean {
    const columnIndex = this.getColumnIndex(column);
    return columnIndex > -1 && columnIndex < (this.columns?.length || 0) - 1;
  }

  canDragColumns(): boolean {
    return (this.columns?.length || 0) > 1;
  }

  get hoveredColumn(): any | null {
    return (this.columns || []).find(
      column => column?.name === this.hoveredColumnName
    ) || null;
  }

  onHeaderDragStart(column: any): void {
    this.draggedColumnName = column?.name || null;
  }

  onHeaderDragEnd(): void {
    this.draggedColumnName = null;
    this.dragOverColumnName = null;
  }

  onHeaderDragOver(event: DragEvent, column: any): void {
    if (!this.draggedColumnName || this.draggedColumnName === column?.name) {
      return;
    }

    event.preventDefault();
    this.dragOverColumnName = column?.name || null;
  }

  onHeaderDrop(event: DragEvent, targetColumn: any): void {
    event.preventDefault();

    const sourceColumn = (this.columns || []).find(
      column => column?.name === this.draggedColumnName
    );
    const previousIndex = this.getColumnIndex(sourceColumn);
    const currentIndex = this.getColumnIndex(targetColumn);

    this.draggedColumnName = null;
    this.dragOverColumnName = null;

    if (
      !sourceColumn ||
      !targetColumn ||
      previousIndex < 0 ||
      currentIndex < 0 ||
      previousIndex === currentIndex
    ) {
      return;
    }

    this.columnReordered.emit({
      column: sourceColumn,
      targetColumn,
      previousIndex,
      currentIndex
    });
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;

    if (!target) {
      return;
    }

    const clickedInsideDropdown = !!target.closest('.entity-dropdown');
    const clickedDropdownTrigger =
      !!target.closest('.cell-add-btn') || !!target.closest('.cell-icon');
    const clickedInsideWorkflowDropdown = !!target.closest('.workflow-dropdown-panel');
    const clickedWorkflowTrigger = !!target.closest('.workflow-dropdown-trigger');

    if (!clickedInsideWorkflowDropdown && !clickedWorkflowTrigger) {
      this.activeDropdown = null;
    }

    if (!clickedInsideDropdown && !clickedDropdownTrigger) {
      this.activeCellRow = null;
      this.activeCellColumn = null;
    }
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  handleViewportChange(): void {
    if (!this.hoveredColumnName) {
      return;
    }

    const headerElement = document.querySelector(
      `.header-cell[data-column-name="${this.escapeColumnName(this.hoveredColumnName)}"]`
    ) as HTMLElement | null;
    const hoveredColumn = (this.columns || []).find(
      column => column?.name === this.hoveredColumnName
    );

    if (!headerElement || !hoveredColumn) {
      this.hoveredColumnName = null;
      return;
    }

    this.setColumnActionsPosition(headerElement.getBoundingClientRect(), hoveredColumn);
  }

  private updateFilteredEntities(): void {
    const normalizedValue = (this.entitySearch || '').trim().toLowerCase();

    this.filteredEntities = (this.entities || []).filter(entity => {
      const label = (
        entity?.header ||
        entity?.label ||
        entity?.templateCode ||
        entity?.name ||
        ''
      ).toLowerCase();

      return label.includes(normalizedValue);
    });
  }

  private scheduleRowHeightSync(): void {
    setTimeout(() => this.syncRowHeights());
  }

  private updateColumnActionsPosition(
    event: MouseEvent | undefined,
    column: any
  ): void {
    const currentTarget = event?.currentTarget as HTMLElement | null;

    if (currentTarget) {
      this.setColumnActionsPosition(currentTarget.getBoundingClientRect(), column);
    }
  }

  private scheduleHoveredColumnHide(column: any): void {
    this.clearHoverHideTimeout();

    this.hoverHideTimeoutId = setTimeout(() => {
      if (this.hoveredColumnName === (column?.name || null)) {
        this.hoveredColumnName = null;
      }
    }, 180);
  }

  private clearHoverHideTimeout(): void {
    if (this.hoverHideTimeoutId) {
      clearTimeout(this.hoverHideTimeoutId);
      this.hoverHideTimeoutId = null;
    }
  }

  private setColumnActionsPosition(rect: DOMRect, column: any): void {
    // Added by Amisha on 05/21/2026 to keep the floating action tray aligned
    // after the delete button became conditional for protected columns.
    const actionCount =
      1 +
      (this.canDeleteColumn(column) ? 1 : 0) +
      (this.canDragColumns() ? 1 : 0) +
      (this.canMoveColumnLeft(column) ? 1 : 0) +
      (this.canMoveColumnRight(column) ? 1 : 0);
    const estimatedWidth = actionCount * 26 + Math.max(0, actionCount - 1) * 8 + 16;
    const viewportPadding = 12;

    this.columnActionsLeft = Math.min(
      Math.max(viewportPadding, rect.left),
      Math.max(viewportPadding, window.innerWidth - estimatedWidth - viewportPadding)
    );
    this.columnActionsTop = Math.max(viewportPadding, rect.top - 38);
  }

  private escapeColumnName(value: string): string {
    return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  private getColumnIndex(column: any): number {
    return (this.columns || []).findIndex(
      existingColumn => existingColumn?.name === column?.name
    );
  }

  private syncRowHeights(): void {
    const workflowRows = this.workflowRowElements?.toArray() || [];
    const dataRows = this.workflowDataRowElements?.toArray() || [];
    const rowCount = Math.min(workflowRows.length, dataRows.length);

    if (!rowCount) {
      return;
    }

    workflowRows.forEach(row => {
      row.nativeElement.style.minHeight = '';
    });

    dataRows.forEach(row => {
      row.nativeElement.style.minHeight = '';
    });

    for (let index = 0; index < rowCount; index++) {
      const workflowHeight = workflowRows[index].nativeElement.offsetHeight;
      const dataHeight = dataRows[index].nativeElement.offsetHeight;
      const targetHeight = Math.max(workflowHeight, dataHeight);

      workflowRows[index].nativeElement.style.minHeight = `${targetHeight}px`;
      dataRows[index].nativeElement.style.minHeight = `${targetHeight}px`;
    }
  }
}
